import {
	Activity,
	Building2,
	CalendarClock,
	Cog,
	Home,
	Inbox,
	KanbanSquare,
	Mail,
	Map,
	Megaphone,
	PhoneCall,
	Settings2,
	Shield,
	Sparkles,
	Users,
	X
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../features/auth/AuthProvider';

type NavItem = { to: string; label: string; icon: typeof Home };

const sections: { title: string; items: NavItem[] }[] = [
	{
		title: 'Workspace',
		items: [
			{ to: '/today', label: 'Today', icon: Home },
			{ to: '/inbox', label: 'Inbox', icon: Inbox }
		]
	},
	{
		title: 'Records',
		items: [
			{ to: '/companies', label: 'Companies', icon: Building2 },
			{ to: '/contacts', label: 'Contacts', icon: Users },
			{ to: '/leads', label: 'Leads', icon: Sparkles },
			{ to: '/pipelines', label: 'Pipelines', icon: KanbanSquare },
			{ to: '/activities', label: 'Activity', icon: Activity }
		]
	},
	{
		title: 'Outreach',
		items: [
			{ to: '/calls', label: 'Call list', icon: PhoneCall },
			{ to: '/dialer', label: 'Power dialer', icon: CalendarClock },
			{ to: '/emails', label: 'Email', icon: Mail },
			{ to: '/campaigns', label: 'Campaigns', icon: Megaphone },
			{ to: '/map', label: 'Territory', icon: Map }
		]
	}
];

const adminItems: NavItem[] = [
	{ to: '/admin', label: 'Admin', icon: Shield },
	{ to: '/admin/system', label: 'System', icon: Cog },
	{ to: '/admin/settings', label: 'Settings', icon: Settings2 }
];

function NavItemLink({ item, onClose }: { item: NavItem; onClose: () => void }) {
	return (
		<NavLink
			to={item.to}
			onClick={onClose}
			className={({ isActive }) =>
				cn(
					'group relative flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition',
					isActive
						? 'bg-accent/10 text-accent'
						: 'text-secondary hover:bg-surface-hover hover:text-primary'
				)
			}
		>
			{({ isActive }) => (
				<>
					{isActive ? <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-accent" /> : null}
					<item.icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-accent' : 'text-muted group-hover:text-secondary')} />
					<span className="flex-1 truncate">{item.label}</span>
				</>
			)}
		</NavLink>
	);
}

function Section({ title, items, onClose }: { title: string; items: NavItem[]; onClose: () => void }) {
	return (
		<div>
			<p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted">{title}</p>
			<div className="space-y-px">
				{items.map((item) => <NavItemLink key={item.to} item={item} onClose={onClose} />)}
			</div>
		</div>
	);
}

export function Sidebar({ mobileOpen, onClose }: { mobileOpen: boolean; onClose: () => void }) {
	const { user } = useAuth();
	const isAdmin = user?.role === 'admin' || (user as { is_admin?: boolean } | null)?.is_admin;

	return (
		<>
			<div
				className={cn(
					'fixed inset-0 z-40 bg-canvas/60 backdrop-blur-sm transition lg:hidden',
					mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
				)}
				onClick={onClose}
			/>
			<aside
				className={cn(
					'fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-subtle bg-surface px-3 py-4 transition-transform duration-200 lg:translate-x-0',
					mobileOpen ? 'translate-x-0' : '-translate-x-full'
				)}
			>
				<div className="flex items-center justify-between px-2 pb-4">
					<div className="flex items-center gap-2">
						<div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent text-accent-contrast font-bold text-sm">G</div>
						<span className="text-sm font-semibold text-primary">GalloCRM</span>
					</div>
					<button type="button" onClick={onClose} className="btn-ghost p-1 lg:hidden">
						<X className="h-4 w-4" />
					</button>
				</div>
				<nav className="flex-1 space-y-4 overflow-y-auto pr-1">
					{sections.map((s) => <Section key={s.title} title={s.title} items={s.items} onClose={onClose} />)}
					{isAdmin ? <Section title="Admin" items={adminItems} onClose={onClose} /> : null}
				</nav>
			</aside>
		</>
	);
}
